import { FC } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { useFormatter } from '@eneco/i18n';
import { useRouter } from '@eneco/next';
import { Badge, Box, Divider, RadioCard, Stack, Text, TextLink } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';

import { inlineCheckmarkReplacement } from '../../../utils/richTextReplacements';
import { getVatPercentage } from '../utils/vatPercentage';

interface ProposalRadioCardProps {
  value: string;
  title: string;
  pricePerMonthVatExclusive: number;
  pricePerMonthVatInclusive: number;
  priceLabel: string;
  taxLabel: string;
  badgeLabel?: string;
  featuresContent: string;
  detailsLink: {
    href: string;
    target?: string;
    text: string;
  };
  type: 'boilerMaintenance' | 'serviceProposal';
}

export const ProposalRadioCard: FC<ProposalRadioCardProps> = ({
  value,
  title,
  pricePerMonthVatExclusive,
  pricePerMonthVatInclusive,
  priceLabel,
  taxLabel,
  badgeLabel,
  featuresContent,
  detailsLink,
  type,
}) => {
  const { format, currency } = useFormatter();

  const vatPercentage = getVatPercentage(pricePerMonthVatExclusive, pricePerMonthVatInclusive);
  const { query } = useRouter();

  const isSoho = query.isSoho === 'true';
  const price = isSoho ? pricePerMonthVatExclusive : pricePerMonthVatInclusive;

  const { trackEventBE } = useTracking();

  return (
    <RadioCard value={value} ariaLabelledby={value} key={value}>
      <Box padding="6">
        <Stack gap="2">
          <Stack direction="row" alignX="justify" gap="2">
            <Stack direction="row" alignY="center" gap="2">
              <RadioCard.Indicator />
              <Text weight="bold" size="BodyL" id={value}>
                {title}
              </Text>
            </Stack>
            <Stack alignX="end">
              <Text weight="bold" size="BodyL">
                {currency.euro(price)} {priceLabel}
              </Text>
              {!isSoho && <Text size="BodyS">{format(taxLabel, { amount: vatPercentage })}</Text>}
            </Stack>
          </Stack>
          {badgeLabel && (
            <Stack.Item>
              <Badge color="alpha" size="S">
                {badgeLabel}
              </Badge>
            </Stack.Item>
          )}
          <RichText html={featuresContent} replacements={inlineCheckmarkReplacement} />
        </Stack>
      </Box>
      <Divider />
      <Box padding="4">
        <Stack alignX="center">
          <TextLink
            href={detailsLink.href}
            target={detailsLink.target}
            onClick={() => {
              trackEventBE(type === 'boilerMaintenance' ? 'be_om_upsell_view_details' : 'be_om_view_bijstand_details', {
                maintenance_type: value,
              });
            }}>
            {detailsLink.text}
          </TextLink>
        </Stack>
      </Box>
    </RadioCard>
  );
};
